import { useAvailableLanguages } from "@src/features/contentDetail/hooks/useAvailableLanguages";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import { langCodeToEnglishName } from "@src/features/app/utils/copy";
import { FC, useMemo } from "react";

type Props = {
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
  variant?: "dark" | "light";
};

const LanguageSelectorDropdown: FC<Props> = ({
  selectedLanguage,
  onLanguageChange,
  variant = "dark",
}) => {
  const {
    allNonEnglishLanguages,
    nonEnglishLanguagesInUse,
    nonEnglishLanguagesNotInUse,
  } = useAvailableLanguages();
  const hasSections = useMemo(
    () =>
      nonEnglishLanguagesInUse.length > 0 &&
      nonEnglishLanguagesNotInUse.length > 0,
    [nonEnglishLanguagesInUse, nonEnglishLanguagesNotInUse],
  );
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={
            variant === "dark"
              ? "border-white bg-transparent text-white shadow-none hover:bg-white/20"
              : undefined
          }
        >
          {langCodeToEnglishName(selectedLanguage)}
          <ChevronDownIcon className="ml-auto size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-80 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto">
        {hasSections ? (
          <>
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              Languages used in your org
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {nonEnglishLanguagesInUse.map((language) => (
                <DropdownMenuItem
                  key={language}
                  onClick={() => onLanguageChange(language)}
                  className="justify-between"
                >
                  {langCodeToEnglishName(language)}
                  {language === selectedLanguage && (
                    <CheckmarkIcon className="size-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              Other languages
            </DropdownMenuLabel>
            <DropdownMenuGroup>
              {nonEnglishLanguagesNotInUse.map((language) => (
                <DropdownMenuItem
                  key={language}
                  onClick={() => onLanguageChange(language)}
                  className="justify-between"
                >
                  {langCodeToEnglishName(language)}
                  {language === selectedLanguage && (
                    <CheckmarkIcon className="size-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </>
        ) : (
          <DropdownMenuGroup>
            {allNonEnglishLanguages.map((language) => (
              <DropdownMenuItem
                key={language}
                onClick={() => onLanguageChange(language)}
                className="justify-between"
              >
                {langCodeToEnglishName(language)}
                {language === selectedLanguage && (
                  <CheckmarkIcon className="size-4" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelectorDropdown;
